import { defineStore } from "pinia";
import { type Node, AnchorType, SatelliteType } from "@livereader/graphly-d3";
import type {
	ConceptFunction,
	ConceptAttribute,
	TemplateWorkspace,
	TemplateTask,
	TemplateProcess,
	ConceptEvent,
} from "@lr-notitia/notitia-designer/schemas";
import { useGraphlyTemplatesStore } from "./graphly-templates";
import { useSymbolsStore } from "./symbols";
import { ref } from "vue";

let graphlyTemplatesStore: ReturnType<typeof useGraphlyTemplatesStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphlyTemplatesStore = useGraphlyTemplatesStore();
	symbolsStore = useSymbolsStore();
}

export const useInspectorStore = defineStore("InspectorStore", () => {
	const history = ref<string[]>([]);
	const historyIndex = ref<number>(-1);

	function pushHistory(id: string) {
		history.value = history.value.slice(0, historyIndex.value + 1);
		history.value.push(id);
		historyIndex.value++;
	}
	function goBack(): string | null {
		if (historyIndex.value > 0) {
			historyIndex.value--;
			return history.value[historyIndex.value];
		}
		return null;
	}
	function goForward(): string | null {
		if (historyIndex.value < history.value.length - 1) {
			historyIndex.value++;
			return history.value[historyIndex.value];
		}
		return null;
	}

	function questionTaskNode(task: TemplateTask, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: task.id + suffix,
			shape: {
				type: "question_task",
				scale: 1,
				url: graphlyTemplatesStore.taskShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				title: symbolsStore.term({ id: task.refersTo, terminology: task.terminology || {} }),
				text: symbolsStore.description({ id: task.refersTo, terminology: task.terminology || {} }),
				mode: "default",
				type: "send",
				color: "#C1C772",
			},
		};
	}
	function systemTaskNode(task: TemplateTask, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: task.id + suffix,
			shape: {
				type: "system_task",
				scale: 1,
				url: graphlyTemplatesStore.taskShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				title: symbolsStore.term({ id: task.refersTo, terminology: task.terminology || {} }),
				text: symbolsStore.description({ id: task.refersTo, terminology: task.terminology || {} }),
				mode: "default",
				type: "service",
				color: "#C1C772",
			},
		};
	}
	function conditionTaskNode(task: TemplateTask, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: task.id + suffix,
			shape: {
				type: "condition_task",
				scale: 1,
				url: graphlyTemplatesStore.genericConditionShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				color: "#C1C772",
				text: symbolsStore.description({ id: task.refersTo, terminology: task.terminology || {} }),
			},
		};
	}
	function logicTaskNode(task: TemplateTask, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore) init();
		return {
			id: task.id + suffix,
			shape: {
				type: "logic_task",
				scale: 1,
				url: graphlyTemplatesStore.logicGateShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				color: "#a6a6a6",
				type: task.concept.split("_")[2],
			},
		};
	}
	function attributeNode(attribute: ConceptAttribute, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: attribute.id + suffix,
			shape: {
				type: "gen-pill",
				scale: 1,
				url: graphlyTemplatesStore.genericPill,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				text: symbolsStore.term({ id: attribute.refersTo, terminology: attribute.terminology || {} }),
				color: "#b3e5fc",
			},
		};
	}
	function processNode(process: TemplateProcess, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: process.id + suffix,
			shape: {
				type: "process",
				scale: 1,
				url: graphlyTemplatesStore.processShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				color: "#EDB656",
				title: symbolsStore.term({ id: process.refersTo, terminology: process.terminology || {} }),
			},
		};
	}
	function eventNode(event: ConceptEvent, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: event.id + suffix,
			shape: {
				type: "event",
				scale: 1,
				url: graphlyTemplatesStore.eventShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				mode: "interrupt",
				type: "notitia",
				color: "",
				alignment: "down",
				note: symbolsStore.term({ id: event.refersTo, terminology: event.terminology || {} }),
			},
		};
	}
	function functionNode(func: ConceptFunction, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: func.id + suffix,
			shape: {
				type: "cell-body",
				scale: 1,
				url: graphlyTemplatesStore.cellBodyShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				name: symbolsStore.term({ id: func.refersTo, terminology: func.terminology || {} }),
			},
		};
	}
	function functionInputNode(sourceId: string, suffix = "_input"): Node {
		if (!graphlyTemplatesStore) init();
		return {
			id: sourceId + suffix,
			shape: {
				type: "cell-satellite",
				scale: 1,
				url: graphlyTemplatesStore.cellSatelliteShape,
			},
			satellite: {
				source: sourceId,
				angle: 270,
				distance: 110,
				type: SatelliteType.Hard,
			},
			payload: {
				type: "input",
			},
		};
	}
	function functionOutputNode(sourceId: string, suffix = "_output"): Node {
		if (!graphlyTemplatesStore) init();
		return {
			id: sourceId + suffix,
			shape: {
				type: "cell-satellite",
				scale: 1,
				url: graphlyTemplatesStore.cellSatelliteShape,
			},
			satellite: {
				source: sourceId,
				angle: 90,
				distance: 110,
				type: SatelliteType.Hard,
			},
			payload: {
				type: "output",
			},
		};
	}
	function workspaceNode(workspace: TemplateWorkspace, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: workspace.id + suffix,
			shape: {
				type: "workspace",
				scale: 2,
				url: graphlyTemplatesStore.workspaceShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				color: "#D6DA80",
				text: symbolsStore.term({ id: workspace.refersTo, terminology: workspace.terminology || {} }),
			},
		};
	}

	return {
		questionTaskNode,
		systemTaskNode,
		conditionTaskNode,
		logicTaskNode,
		attributeNode,
		processNode,
		eventNode,
		functionNode,
		functionInputNode,
		functionOutputNode,
		workspaceNode,
		history,
		historyIndex,
		pushHistory,
		goBack,
		goForward,
	};
});
